import React, { useState, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';

function playBeep(freq = 660, duration = 0.1) {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'square'; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.3, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + duration);
    osc.connect(gain); gain.connect(ac.destination);
    osc.start(); osc.stop(ac.currentTime + duration);
    setTimeout(() => ac.close(), (duration + 0.1) * 1000);
  } catch {}
}

// Generate a rhythm: array of {beat: bool} over 8 slots at 300ms each
function genRhythm(complexity = 3) {
  const beats = [];
  for (let i = 0; i < 8; i++) {
    if (i === 0) { beats.push(true); continue; }
    beats.push(Math.random() < (0.3 + complexity * 0.08));
  }
  return beats;
}

const ROUNDS = 5;
const SLOT_MS = 380;

function RhythmCopyGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [phase, setPhase] = useState('start'); // start | playing | wait | input | result | done
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [rhythm, setRhythm] = useState([]);
  const [inputBeats, setInputBeats] = useState([]);
  const [activeBeat, setActiveBeat] = useState(-1);
  const [activeInput, setActiveInput] = useState(false);
  const [accuracy, setAccuracy] = useState(null);
  const timersRef = useRef([]);

  const clearTimers = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };

  const playRhythm = (r) => {
    setPhase('playing'); setActiveBeat(-1);
    r.forEach((on, i) => {
      const t = setTimeout(() => {
        setActiveBeat(i);
        if (on) playBeep(660, 0.08);
        if (i === r.length - 1) {
          setTimeout(() => { setActiveBeat(-1); setPhase('wait'); setTimeout(() => startInput(r), 600); }, 200);
        }
      }, i * SLOT_MS);
      timersRef.current.push(t);
    });
  };

  const startInput = (r) => {
    setPhase('input'); setInputBeats([]); let collected = [];
    let beat = 0;
    const interval = setInterval(() => {
      beat++;
      if (beat >= r.length + 1) {
        clearInterval(interval);
        // Score
        let hits = 0;
        r.forEach((on, i) => { if (on === (collected[i] || false)) hits++; });
        const acc = Math.round((hits / r.length) * 100);
        setAccuracy(acc);
        setPhase('result');
        const pts = Math.floor(acc / 100 * 10);
        setScore(s => s + pts);
        if (acc > 70) playBeep(880, 0.15); else playBeep(200, 0.3);
        const next = round + 1;
        if (next >= ROUNDS) {
          setTimeout(() => { saveGameScore('rhythmcopy', Math.round(((score + pts) / (ROUNDS * 10)) * 100)); setPhase('done'); }, 1200);
        } else {
          setTimeout(() => { setRound(next); const nr = genRhythm(2 + Math.floor(next / 2)); setRhythm(nr); playRhythm(nr); }, 1200);
        }
      }
    }, SLOT_MS);
    timersRef.current.push(interval);

    // Capture taps via state updates — use ref for real-time collection
    const tapRef = (i) => { collected[i] = true; };
    window._rhythmTap = tapRef;
    setInputBeats([]); // reset display
  };

  const handleTap = () => {
    if (phase !== 'input') return;
    const now = Date.now();
    if (!window._rhythmTapStart) window._rhythmTapStart = now;
    const slot = Math.floor((now - window._rhythmTapStart) / SLOT_MS);
    if (slot < rhythm.length && window._rhythmTap) window._rhythmTap(slot);
    setInputBeats(p => [...p, slot]);
    setActiveInput(true);
    setTimeout(() => setActiveInput(false), 120);
    playBeep(550, 0.08);
  };

  const startGame = () => {
    clearTimers();
    window._rhythmTapStart = null;
    const r = genRhythm(2);
    setRhythm(r); setRound(0); setScore(0); setAccuracy(null); setInputBeats([]);
    setTimeout(() => playRhythm(r), 300);
  };

  if (phase === 'done') {
    return (
      <div>
        <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>■ {nl ? 'Stop' : 'Stop'}</button>
        <div style={{ ...s_GC({ padding: 24, textAlign: 'center' }) }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🥁</div>
          <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Klaar!' : 'Done!'}</div>
          <div style={{ color: _T.accent, fontSize: 32, fontWeight: 700 }}>{score}/{ROUNDS * 10}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 16 }}>{nl ? 'punten' : 'points'}</div>
          <button style={s_PB({ width: '100%' })} onClick={startGame}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>■ {nl ? 'Stop' : 'Stop'}</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ color: C.silver, fontSize: 12 }}>{nl ? `Ronde ${round + 1}/${ROUNDS}` : `Round ${round + 1}/${ROUNDS}`}</span>
        <span style={{ color: _T.accent, fontSize: 12, fontWeight: 700 }}>{score} {nl ? 'ptn' : 'pts'}</span>
      </div>

      {/* Beat visualizer */}
      <div style={{ ...s_GC({ padding: 16, marginBottom: 14 }), display: 'flex', gap: 6, justifyContent: 'center' }}>
        {(rhythm.length > 0 ? rhythm : Array(8).fill(false)).map((on, i) => (
          <div key={i} style={{ width: 28, height: 28, borderRadius: 8, border: `2px solid ${on ? '#C8A84B88' : '#8899BB33'}`, background: activeBeat === i ? (on ? '#C8A84B' : '#5B9ED055') : (on ? '#C8A84B33' : '#8899BB11'), transition: 'background 0.05s' }} />
        ))}
      </div>

      <div style={{ ...s_GC({ padding: 16, textAlign: 'center', marginBottom: 14 }) }}>
        {phase === 'start' && <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.5 }}>{nl ? 'Luister naar het ritme en tik het na.' : 'Listen to the rhythm and copy it by tapping.'}</div>}
        {phase === 'playing' && <div style={{ color: C.orange, fontWeight: 700 }}>{nl ? '👂 Luister...' : '👂 Listen...'}</div>}
        {phase === 'wait' && <div style={{ color: C.silver }}>{nl ? 'Klaar om te tikken...' : 'Get ready to tap...'}</div>}
        {phase === 'input' && <div style={{ color: C.teal, fontWeight: 700 }}>{nl ? '🥁 Tik het ritme na!' : '🥁 Copy the rhythm!'}</div>}
        {phase === 'result' && <div style={{ color: (accuracy || 0) > 70 ? C.teal : C.orange, fontWeight: 700 }}>{nl ? `Nauwkeurigheid: ${accuracy}%` : `Accuracy: ${accuracy}%`}</div>}
      </div>

      {phase === 'start' && <button style={s_PB({ width: '100%' })} onClick={startGame}>{nl ? 'Start' : 'Start'}</button>}
      {phase === 'input' && (
        <button onPointerDown={handleTap}
          style={{ width: '100%', height: 120, borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 36, background: activeInput ? '#C8A84B44' : _T.bg4, transition: 'background 0.1s' }}>
          🥁
        </button>
      )}
    </div>
  );
}

export default RhythmCopyGame;
